import React, { useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useCreateProjectMutation, useGetProjectByRefNameQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { getFormErrorMessage } from "../utils/utils";

type ProjectDropDownProps = {
  projectId: string | null;
  setProjectId: (projectId: string) => void;
  companyRef: string;
};

type ProjectFormValues = {
  title: string;
};

const schema = z.object({
  title: z.string().min(1, { message: "Required" }),
});

const ProjectDropDown: React.FC<ProjectDropDownProps> = ({
  projectId,
  setProjectId,
  companyRef,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(schema),
  });

  const op = useRef<OverlayPanel>(null);
  const { data } = useGetProjectByRefNameQuery({
    variables: { refName: companyRef },
  });

  const [mutateFunction] = useCreateProjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Project created successfully",
        life: 1000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetProjectByRefName"],
  });

  const handleCreateProject: SubmitHandler<ProjectFormValues> = projectData => {
    mutateFunction({
      variables: {
        project: { companyReference: companyRef, title: projectData.title },
      },
    });
    reset();
    op.current?.hide();
  };

  const renderTitleControl = (field: ControllerRenderProps<ProjectFormValues, any>) => (
    <InputLabelWrapper labelName="Project name" className="w-full">
      <div className="p-inputgroup">
        <InputText
          value={field.value}
          placeholder="Project name"
          onChange={e => field.onChange(e.target.value)}
        />
        <Button label="Create" className="p-button-success" type="submit" />
      </div>
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );

  return (
    <div className="p-inputgroup w-15rem">
      <Dropdown
        value={projectId}
        options={data?.mdmProjectsByCompany || []}
        optionLabel="title"
        optionValue="id"
        onChange={e => setProjectId(e.value)}
        placeholder="Select project"
        className="w-13rem"
      />
      <Button
        icon="pi pi-plus"
        onClick={e => op.current?.toggle(e)}
        className="p-inputgroup-addon p-button p-button-success p-button-text bg-gray-200 border-gray-300"
      />
      <OverlayPanel ref={op}>
        <form onSubmit={handleSubmit(handleCreateProject)}>
          <Controller
            defaultValue=""
            name="title"
            control={control}
            render={({ field }) => renderTitleControl(field)}
          />
        </form>
      </OverlayPanel>
    </div>
  );
};

export default ProjectDropDown;
